import { useEffect, useState } from "react";
import { useApiClient } from "@/lib/apiClient";
import { X, AlertTriangle } from "lucide-react";

export function DayOverviewModal({ date, onClose }) {
  const api = useApiClient();
  const [range, setRange] = useState("day");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null); setError("");
    api.get(`/agenda/day-overview?date=${date}&range=${range}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Errore"));
  }, [date, range]);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const fmtDate = (iso) => {
    try { return new Date(iso + "T00:00:00Z").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }); }
    catch { return iso; }
  };
  const fmtShort = (iso) => {
    try { return new Date(iso + "T00:00:00Z").toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "UTC" }); }
    catch { return iso; }
  };
  const fmtMonth = (iso) => {
    try { return new Date(iso + "T00:00:00Z").toLocaleDateString("it-IT", { month: "long", year: "numeric", timeZone: "UTC" }); }
    catch { return iso; }
  };
  const rangeTitle = () => {
    if (!data) return fmtDate(date);
    if (data.range === "day") return fmtDate(data.date);
    if (data.range === "week") return `${fmtShort(data.date_from)} — ${fmtShort(data.date_to)}`;
    return fmtMonth(data.date_from);
  };
  const rangeLabel = () => data?.range === "week" ? "Panoramica settimanale" : data?.range === "month" ? "Panoramica mensile" : "Panoramica del giorno";

  const donut = (pct, color) => {
    const p = Math.min(100, Math.max(0, pct));
    return (
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
          <circle cx="20" cy="20" r="16" fill="none" stroke="#F1F5F9" strokeWidth="4" />
          <circle cx="20" cy="20" r="16" fill="none" stroke={color} strokeWidth="4"
                  strokeDasharray={`${(p / 100) * 100.5} 100.5`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-black">{p.toFixed(0)}%</span>
        </div>
      </div>
    );
  };
  const eur0 = (n) => `€ ${(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

  return (
    <div className="fixed inset-0 z-[85] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="day-overview-modal">
      <div className="sp-card w-full max-w-2xl p-8 my-8 space-y-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow">{rangeLabel()}</div>
            <h2 className="font-display text-2xl font-black capitalize mt-1">{rangeTitle()}</h2>
            {data?.range !== "day" && data?.days && (
              <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">{data.days} giorn{data.days === 1 ? "o" : "i"} inclusi</div>
            )}
          </div>
          <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1 flex-shrink-0" data-testid="overview-range-toggle">
            {[["day", "Giorno"], ["week", "Settimana"], ["month", "Mese"]].map(([k, l]) => (
              <button key={k} data-testid={`overview-range-${k}`} onClick={() => setRange(k)}
                      className={`px-3 py-1 rounded-full text-xs font-bold ${range === k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {error && <div className="text-sm text-[color:var(--sp-danger)]">{error}</div>}
        {!data && !error && <div className="text-sm text-[color:var(--sp-text-muted)]">Caricamento…</div>}

        {data && data.closed && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            {range === "day" ? "Il salone risulta chiuso in questa data (chiusura settimanale o straordinaria)." : "Il salone risulta chiuso in tutto il periodo selezionato."}
          </div>
        )}

        {data && !data.closed && data.workable_minutes === 0 && (
          <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 text-sm text-blue-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              Gli orari di apertura del salone non sono configurati per questo periodo. Vai in <b>Impostazioni &gt; Orari</b> per definire i turni.
            </div>
          </div>
        )}

        {data && !data.closed && data.workable_minutes > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="day-overview-donuts">
              <div className="rounded-xl border border-[color:var(--sp-border)] p-4 flex items-center gap-4">
                {donut(data.occupancy_pct, "#3B82F6")}
                <div>
                  <div className="label-eyebrow">Ore occupate</div>
                  <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                    {Math.round(data.booked_minutes / 60)}h su {Math.round(data.workable_minutes / 60)}h lavorabili
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-[color:var(--sp-border)] p-4 flex items-center gap-3">
                <div className="w-24 h-24 flex flex-col items-center justify-center bg-emerald-50 rounded-xl px-2">
                  <div className="text-xl font-black text-emerald-600 leading-tight text-center">{eur0(data.confirmed_revenue)}</div>
                </div>
                <div>
                  <div className="label-eyebrow">Ricavo previsto</div>
                  <div className="text-xs text-[color:var(--sp-text-muted)] mt-1">
                    Somma dei servizi degli appuntamenti confermati nel periodo.
                  </div>
                </div>
              </div>
              <div className="rounded-xl border border-[color:var(--sp-border)] p-4 flex items-center gap-4">
                <div className="w-24 h-24 flex flex-col items-center justify-center">
                  <div className="text-3xl font-black text-[color:var(--sp-brand)]">{data.appointments_total}</div>
                  <div className="text-[10px] uppercase font-bold text-[color:var(--sp-text-muted)] tracking-wider">appt</div>
                </div>
                <div>
                  <div className="label-eyebrow">Appuntamenti</div>
                  <div className="text-xs text-[color:var(--sp-text-muted)] mt-1 space-y-0.5">
                    <div>✓ {data.counts?.completed || 0} completati</div>
                    <div>⏱ {data.counts?.scheduled || 0} in programma</div>
                    <div className="text-red-600">✗ {(data.counts?.cancelled || 0) + (data.counts?.no_show || 0)} disdette / no-show</div>
                  </div>
                </div>
              </div>
            </div>

            {data.by_staff?.length > 0 && (
              <div>
                <div className="label-eyebrow mb-3">Performance staff</div>
                <div className="space-y-2" data-testid="day-overview-staff">
                  {data.by_staff.map((row) => {
                    const workedH = (row.workable_minutes / 60);
                    const usedH = (row.service_minutes / 60);
                    return (
                      <div key={row.staff_id} className="rounded-xl border border-[color:var(--sp-border)] p-3" data-testid={`day-overview-staff-row-${row.staff_id}`}>
                        <div className="flex items-center gap-3">
                          <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: row.color }} />
                          <span className="font-bold text-sm flex-1 truncate">{row.name}</span>
                          <div className="text-right">
                            <div className="text-sm font-black text-emerald-600 tabular-nums" data-testid={`day-overview-staff-total-${row.staff_id}`}>{eur0(row.total_revenue)}</div>
                            <div className="text-[10px] uppercase tracking-wider text-[color:var(--sp-text-muted)] font-bold">totale</div>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                          <div className="rounded-lg bg-[color:var(--sp-subtle)] px-2 py-1.5">
                            <div className="text-[9px] uppercase tracking-wider text-[color:var(--sp-text-muted)] font-bold">Lavorabili</div>
                            <div className="font-black tabular-nums">{workedH.toFixed(1)}h</div>
                          </div>
                          <div className="rounded-lg bg-[color:var(--sp-subtle)] px-2 py-1.5">
                            <div className="text-[9px] uppercase tracking-wider text-[color:var(--sp-text-muted)] font-bold">Utilizzate</div>
                            <div className="font-black tabular-nums">{usedH.toFixed(1)}h <span className="text-[10px] font-bold" style={{ color: row.color }}>· {row.occupancy_pct}%</span></div>
                          </div>
                          <div className="rounded-lg bg-emerald-50 px-2 py-1.5">
                            <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">Servizi</div>
                            <div className="font-black tabular-nums text-emerald-700">{eur0(row.service_revenue)}</div>
                          </div>
                          <div className="rounded-lg bg-amber-50 px-2 py-1.5">
                            <div className="text-[9px] uppercase tracking-wider text-amber-700 font-bold">Extra</div>
                            <div className="font-black tabular-nums text-amber-700">{eur0(row.extra_revenue)}</div>
                          </div>
                        </div>
                        {workedH > 0 && (
                          <div className="mt-2 h-1.5 rounded-full bg-[color:var(--sp-subtle)] overflow-hidden">
                            <div className="h-full transition-all" style={{ width: `${Math.min(100, row.occupancy_pct)}%`, background: row.color }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {data.by_service?.length > 0 && (
              <div>
                <div className="label-eyebrow mb-3">Per servizio</div>
                <div className="space-y-1.5" data-testid="day-overview-services">
                  {data.by_service.map((row) => (
                    <div key={row.service_id} className="flex items-center gap-3 text-sm py-1.5 border-b border-[color:var(--sp-border)] last:border-0">
                      <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: row.color }} />
                      <span className="flex-1 truncate">{row.name}</span>
                      <span className="font-bold text-[color:var(--sp-text-secondary)] tabular-nums">×{row.count}</span>
                      <span className="text-xs text-[color:var(--sp-text-muted)] w-16 text-right tabular-nums">{eur0(row.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
